/**
 * GameScene - Main gameplay scene with complete game loop
 * Implements tower-building with Contact Freeze, Slide, scoring, and win/loss conditions.
 * Features "Tactile Minimalism" visual aesthetic.
 */

import Phaser from 'phaser';
import { SCENES, COLORS, GAME, GAMEPLAY, PHYSICS, SLIDE } from '../config/gameConfig';
import { SpawnManager } from '../systems/SpawnManager';
import { GameStateMachine, GameState } from '../systems/GameStateMachine';
import { FreezeSystem } from '../systems/FreezeSystem';
import { SlideSystem } from '../systems/SlideSystem';
import { CenterOfMassIndicator } from '../systems/CenterOfMassIndicator';
import { HeightTracker } from '../systems/HeightTracker';
import { LossDetector } from '../systems/LossDetector';
import { ScoreManager } from '../systems/ScoreManager';
import { Background } from '../systems/Background';
import { PaperTexture } from '../systems/PaperTexture';
import { AnimationSystem } from '../systems/AnimationSystem';
import { AudioManager } from '../systems/AudioManager';
import { HapticsManager } from '../systems/HapticsManager';
import { SlideHapticsController } from '../systems/SlideHapticsController';
import { HUD } from '../ui/HUD';
import { SettingsPanel } from '../ui/SettingsPanel';
import { ShapeObject, ShapeFactory } from '../entities/ShapeFactory';
import { Vector2 } from '../../utils/vectorMath';
import { GameOverData } from './GameOverScene';

/**
 * Main gameplay scene
 */
export class GameScene extends Phaser.Scene {
  // Core systems
  private spawnManager!: SpawnManager;
  private stateMachine!: GameStateMachine;
  private freezeSystem!: FreezeSystem;
  private slideSystem!: SlideSystem;
  private comIndicator!: CenterOfMassIndicator;

  // Game loop systems
  private heightTracker!: HeightTracker;
  private lossDetector!: LossDetector;
  private scoreManager!: ScoreManager;
  private hud!: HUD;

  // Visual systems
  private background!: Background;
  private paperTexture!: PaperTexture;
  private animationSystem!: AnimationSystem;

  // Audio and haptics
  private audioManager!: AudioManager;
  private haptics!: HapticsManager;
  private slideHaptics!: SlideHapticsController;
  private slideSound: Phaser.Sound.BaseSound | null = null;
  private settingsPanel!: SettingsPanel;

  // Game objects
  private activeShape: ShapeObject | null = null;
  private placedShapes: ShapeObject[] = [];
  private platformBody!: MatterJS.BodyType;
  private platformGraphics!: Phaser.GameObjects.Graphics;
  private platformShadow!: Phaser.GameObjects.Graphics;

  // Collision tracking
  private lastCollisionNormal: Vector2 | null = null;

  // Visual elements
  private freezeOverlay!: Phaser.GameObjects.Graphics;
  private stateText!: Phaser.GameObjects.Text;
  private slideHint!: Phaser.GameObjects.Graphics;

  // Game state flags
  private isGameOver: boolean = false;

  // Shadow offset for paper aesthetic
  private shadowOffset = ShapeFactory.getShadowOffset();

  constructor() {
    super({ key: SCENES.GAME });
  }

  preload(): void {
    // Initialize audio manager and preload audio assets
    this.audioManager = new AudioManager(this);
    this.audioManager.preload();
  }

  create(): void {
    // Don't use background color - we'll draw a gradient
    this.cameras.main.setBackgroundColor(COLORS.BACKGROUND);

    // Reset game over flag
    this.isGameOver = false;

    // Initialize visual systems first (background layers)
    this.background = new Background(this);
    this.paperTexture = new PaperTexture(this);
    this.animationSystem = new AnimationSystem(this);

    // Initialize audio and haptics
    this.audioManager.create();
    this.haptics = new HapticsManager();
    this.slideHaptics = new SlideHapticsController(this.haptics);

    // Initialize core systems
    this.stateMachine = new GameStateMachine();
    this.freezeSystem = new FreezeSystem(this);
    this.spawnManager = new SpawnManager(this);
    this.slideSystem = new SlideSystem(this);
    this.comIndicator = new CenterOfMassIndicator(this, SLIDE.COM_WARNING_THRESHOLD);

    // Initialize game loop systems
    this.heightTracker = new HeightTracker(this, GAMEPLAY.ZENITH_LINE_Y, GAMEPLAY.STABILITY_TIME);
    this.lossDetector = new LossDetector({
      floorY: GAMEPLAY.PLATFORM_Y + GAMEPLAY.PLATFORM_HEIGHT,
      leftBoundary: -50,
      rightBoundary: GAME.WIDTH + 50,
      fallGrace: 100,
    });
    this.scoreManager = new ScoreManager();
    this.hud = new HUD(this);

    // Setup physics world
    this.setupPhysicsWorld();

    // Create game elements
    this.createPlatform();
    this.drawZenithLine();
    this.createBackButton();
    this.createFreezeOverlay();
    this.createStateIndicator();
    this.createSlideHint();

    // Setup collision detection
    this.setupCollisionDetection();

    // Setup input handling
    this.setupInput();

    // Setup state callbacks
    this.setupStateCallbacks();

    // Initialize HUD
    this.hud.updateHighScore(this.scoreManager.getHighScore());
    this.hud.updateScore(0);
    this.hud.updatePiecesCount(0);

    // Create settings panel (after audio/haptics are initialized)
    this.settingsPanel = new SettingsPanel(this, this.audioManager, this.haptics);

    // Start game
    this.spawnNextPiece();
  }

  update(): void {
    if (this.isGameOver) return;

    // Update graphics positions to follow physics bodies
    this.updateShapeGraphics();

    // Update CoM indicator during freeze
    if (this.stateMachine.isState(GameState.FROZEN) && this.activeShape) {
      const allBodies = [
        ...this.freezeSystem.getPlacedBodies(),
        this.activeShape.body,
      ];
      this.comIndicator.update(allBodies, GAME.WIDTH / 2);
    }

    // Check game conditions during stabilizing phase
    if (this.stateMachine.isState(GameState.STABILIZING)) {
      this.checkGameConditions();
    }

    // Update height bar
    const placedBodies = this.freezeSystem.getPlacedBodies();
    const highestPoint = this.heightTracker.getHighestPoint(placedBodies);
    this.hud.updateHeightBar(
      highestPoint,
      GAMEPLAY.ZENITH_LINE_Y,
      GAMEPLAY.PLATFORM_Y
    );

    // Update stability countdown
    if (this.heightTracker.isTimerActive()) {
      const remaining = this.heightTracker.getTimeRemaining() / 1000;
      this.hud.showStabilityCountdown(remaining);
    } else {
      this.hud.hideStabilityCountdown();
    }
  }

  /**
   * Check win and loss conditions
   */
  private checkGameConditions(): void {
    const placedBodies = this.freezeSystem.getPlacedBodies();

    // Check win condition
    this.heightTracker.checkWinCondition(placedBodies, () => {
      this.handleWin();
    });

    // Check loss conditions
    this.lossDetector.checkLossConditions(placedBodies, (reason) => {
      this.handleLoss(reason);
    });
  }

  /**
   * Handle win condition
   */
  private handleWin(): void {
    if (this.isGameOver) return;
    this.isGameOver = true;

    // Stop physics
    this.matter.world.pause();

    // Audio and haptic feedback
    this.audioManager.playWin();
    this.haptics.winCelebration();

    // Play win celebration
    const graphicsList = this.placedShapes.map((s) => s.graphics);
    this.animationSystem.playWinCelebration(graphicsList);

    // Transition to game over
    const data: GameOverData = {
      score: this.scoreManager.getCurrentScore(),
      highScore: this.scoreManager.getHighScore(),
      piecesPlaced: this.scoreManager.getPiecesPlaced(),
      reason: 'Tower reached the Zenith!',
      isWin: true,
    };

    this.time.delayedCall(1500, () => {
      this.cleanupScene();
      this.scene.start(SCENES.GAME_OVER, data);
    });
  }

  /**
   * Handle loss condition
   */
  private handleLoss(reason: string): void {
    if (this.isGameOver) return;
    this.isGameOver = true;

    // Stop physics
    this.matter.world.pause();

    // Audio and haptic feedback
    this.audioManager.playFall();
    this.haptics.loseFeedback();

    // Play collapse animation
    const graphicsList = this.placedShapes.map((s) => s.graphics);
    this.animationSystem.playCollapseAnimation(graphicsList);

    // Transition to game over
    const data: GameOverData = {
      score: this.scoreManager.getCurrentScore(),
      highScore: this.scoreManager.getHighScore(),
      piecesPlaced: this.scoreManager.getPiecesPlaced(),
      reason,
      isWin: false,
    };

    this.time.delayedCall(1000, () => {
      this.cleanupScene();
      this.scene.start(SCENES.GAME_OVER, data);
    });
  }

  /**
   * Configure the Matter.js physics world
   */
  private setupPhysicsWorld(): void {
    this.matter.world.setBounds(0, 0, GAME.WIDTH, GAME.HEIGHT);
    this.matter.world.setGravity(0, PHYSICS.GRAVITY_Y);

    if (import.meta.env.DEV && GAME.DEBUG_PHYSICS) {
      this.matter.world.createDebugGraphic();
    }
  }

  /**
   * Create the platform with physics body and paper aesthetic
   */
  private createPlatform(): void {
    const x = GAME.WIDTH / 2;
    const y = GAMEPLAY.PLATFORM_Y + GAMEPLAY.PLATFORM_HEIGHT / 2;
    const width = GAMEPLAY.PLATFORM_WIDTH;
    const height = GAMEPLAY.PLATFORM_HEIGHT;

    this.platformBody = this.matter.add.rectangle(x, y, width, height, {
      isStatic: true,
      friction: PHYSICS.FRICTION_STATIC,
      frictionStatic: PHYSICS.FRICTION_STATIC,
      label: 'platform',
    });

    // Platform shadow
    this.platformShadow = this.add.graphics();
    this.platformShadow.setDepth(1);
    this.platformShadow.fillStyle(0x000000, 0.15);
    this.platformShadow.fillRoundedRect(
      x - width / 2 + 4,
      y - height / 2 + 5,
      width,
      height,
      6
    );

    // Main platform visual
    this.platformGraphics = this.add.graphics();
    this.platformGraphics.setDepth(2);

    // Charcoal color with rounded corners
    this.platformGraphics.fillStyle(COLORS.PLATFORM, 1);
    this.platformGraphics.fillRoundedRect(
      x - width / 2,
      y - height / 2,
      width,
      height,
      6
    );

    // Subtle top highlight
    this.platformGraphics.fillStyle(0xffffff, 0.08);
    this.platformGraphics.fillRoundedRect(
      x - width / 2 + 3,
      y - height / 2 + 2,
      width - 6,
      4,
      2
    );

    // Subtle border
    this.platformGraphics.lineStyle(1, 0x000000, 0.1);
    this.platformGraphics.strokeRoundedRect(
      x - width / 2,
      y - height / 2,
      width,
      height,
      6
    );
  }

  /**
   * Draw the Zenith line (win target) with improved styling
   */
  private drawZenithLine(): void {
    const graphics = this.add.graphics();
    graphics.setDepth(3);

    // Soft glow effect behind line
    graphics.fillStyle(COLORS.ZENITH_LINE, 0.1);
    graphics.fillRect(0, GAMEPLAY.ZENITH_LINE_Y - 10, GAME.WIDTH, 20);

    // Dashed line effect
    for (let x = 0; x < GAME.WIDTH; x += 20) {
      graphics.lineStyle(2, COLORS.ZENITH_LINE, 0.5);
      graphics.lineBetween(x, GAMEPLAY.ZENITH_LINE_Y, x + 10, GAMEPLAY.ZENITH_LINE_Y);
    }

    // Label with improved styling
    const label = this.add.text(15, GAMEPLAY.ZENITH_LINE_Y - 28, 'ZENITH', {
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '13px',
      color: '#9BA4B0',
      fontStyle: 'bold',
      stroke: '#ffffff',
      strokeThickness: 2,
    });
    label.setDepth(100);
    label.setAlpha(0.8);
  }

  /**
   * Create back to menu button with improved styling
   */
  private createBackButton(): void {
    const button = this.add.text(20, 40, '← Menu', {
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '18px',
      color: '#636E72',
      stroke: '#ffffff',
      strokeThickness: 2,
    });
    button.setInteractive({ useHandCursor: true });
    button.setDepth(100);

    button.on('pointerdown', () => {
      this.audioManager.playButtonTap();
      this.haptics.buttonTap();
      this.cleanupScene();
      this.scene.start(SCENES.MENU);
    });

    button.on('pointerover', () => {
      button.setColor('#2D3436');
      button.setScale(1.05);
    });
    button.on('pointerout', () => {
      button.setColor('#636E72');
      button.setScale(1);
    });

    // Settings button
    const settingsBtn = this.add.text(GAME.WIDTH - 20, 40, '⚙', {
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '24px',
      color: '#636E72',
      stroke: '#ffffff',
      strokeThickness: 2,
    });
    settingsBtn.setOrigin(1, 0);
    settingsBtn.setInteractive({ useHandCursor: true });
    settingsBtn.setDepth(100);

    settingsBtn.on('pointerdown', () => {
      this.audioManager.playButtonTap();
      this.haptics.buttonTap();
      this.settingsPanel.show();
    });

    settingsBtn.on('pointerover', () => {
      settingsBtn.setColor('#2D3436');
      settingsBtn.setScale(1.1);
    });
    settingsBtn.on('pointerout', () => {
      settingsBtn.setColor('#636E72');
      settingsBtn.setScale(1);
    });
  }

  /**
   * Create the freeze overlay with subtle vignette effect
   */
  private createFreezeOverlay(): void {
    const { width, height } = this.scale;

    this.freezeOverlay = this.add.graphics();
    this.freezeOverlay.setDepth(50);
    this.freezeOverlay.setAlpha(0);

    // Simple semi-transparent overlay (very subtle)
    this.freezeOverlay.fillStyle(0x2d3436, 0.15);
    this.freezeOverlay.fillRect(0, 0, width, height);
  }

  /**
   * Create state indicator text
   */
  private createStateIndicator(): void {
    this.stateText = this.add.text(GAME.WIDTH / 2, 100, '', {
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '16px',
      color: '#636E72',
      align: 'center',
      stroke: '#ffffff',
      strokeThickness: 2,
    });
    this.stateText.setOrigin(0.5, 0.5);
    this.stateText.setAlpha(0.9);
    this.stateText.setDepth(100);
  }

  /**
   * Create slide hint indicator (shows slide direction)
   */
  private createSlideHint(): void {
    this.slideHint = this.add.graphics();
    this.slideHint.setDepth(99);
    this.slideHint.setVisible(false);
  }

  /**
   * Setup collision event detection
   */
  private setupCollisionDetection(): void {
    this.matter.world.on(
      'collisionstart',
      (event: Phaser.Physics.Matter.Events.CollisionStartEvent) => {
        // Only process collisions in FALLING state
        if (!this.stateMachine.isState(GameState.FALLING)) return;
        if (!this.activeShape) return;

        const activeBody = this.activeShape.body;

        for (const pair of event.pairs) {
          const { bodyA, bodyB } = pair;

          // Check if active body is involved
          if (bodyA === activeBody || bodyB === activeBody) {
            const otherBody = bodyA === activeBody ? bodyB : bodyA;

            // Freeze if colliding with platform or placed piece
            if (
              otherBody === this.platformBody ||
              this.freezeSystem.isPlacedBody(otherBody)
            ) {
              // Store collision normal for sliding
              const collision = pair as unknown as {
                collision: { normal: { x: number; y: number } };
              };
              if (collision.collision && collision.collision.normal) {
                let normal = collision.collision.normal;
                if (bodyA === activeBody) {
                  normal = { x: -normal.x, y: -normal.y };
                }
                this.lastCollisionNormal = { x: normal.x, y: normal.y };
              } else {
                this.lastCollisionNormal = { x: 0, y: -1 };
              }

              // Play collision impact animation
              this.animationSystem.playCollisionImpact(this.activeShape.graphics);

              this.enterFreezeMode();
              break;
            }
          }
        }
      }
    );
  }

  /**
   * Setup input handling
   */
  private setupInput(): void {
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (this.isGameOver) return;
      if (pointer.y < 80 && pointer.x < 100) return;

      if (this.stateMachine.isState(GameState.FROZEN)) {
        this.slideSystem.handlePointerDown(pointer);
      }
    });

    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (this.isGameOver) return;

      if (this.stateMachine.isState(GameState.FROZEN)) {
        const placedBodies = this.freezeSystem.getPlacedBodies();
        this.slideSystem.handlePointerMove(pointer, placedBodies);

        // Update slide haptics
        if (this.activeShape) {
          this.slideHaptics.updateSlide(this.activeShape.body.position.x);
        }
      }
    });

    this.input.on('pointerup', () => {
      if (this.isGameOver) return;

      if (this.stateMachine.isState(GameState.FROZEN)) {
        this.slideSystem.handlePointerUp();
        this.exitFreezeMode();
      }
    });
  }

  /**
   * Setup callbacks for state transitions
   */
  private setupStateCallbacks(): void {
    this.stateMachine.onEnter(GameState.FALLING, () => {
      this.updateStateText('');
    });

    this.stateMachine.onEnter(GameState.FROZEN, () => {
      this.showFreezeVisuals();
      this.updateStateText('Drag to slide, release to drop');
    });

    this.stateMachine.onExit(GameState.FROZEN, () => {
      this.hideFreezeVisuals();
    });

    this.stateMachine.onEnter(GameState.STABILIZING, () => {
      this.updateStateText('');
    });

    this.stateMachine.onEnter(GameState.SPAWNING, () => {
      this.updateStateText('');
    });
  }

  /**
   * Enter freeze mode when piece lands
   */
  private enterFreezeMode(): void {
    if (!this.activeShape) return;

    // Freeze physics
    this.freezeSystem.freeze(this.activeShape.body);

    // Start slide with collision normal
    const normal = this.lastCollisionNormal || { x: 0, y: -1 };
    this.slideSystem.startSlide(this.activeShape.body, normal);

    // Audio and haptic feedback for collision
    this.audioManager.playImpact();
    this.haptics.impactCollision();

    // Start slide audio and haptics
    this.slideSound = this.audioManager.playSlideStart();
    this.slideHaptics.startSlide(this.activeShape.body.position.x);

    // Show CoM indicator
    this.comIndicator.show();

    // Show slide hint
    this.showSlideHint();

    // Play freeze enter animation
    this.animationSystem.playFreezeEnterAnimation(this.activeShape.graphics);

    // Transition state
    this.stateMachine.transition(GameState.FROZEN);
  }

  /**
   * Exit freeze mode and resume physics
   */
  private exitFreezeMode(): void {
    if (!this.activeShape) return;

    // Stop slide audio and haptics
    this.audioManager.playSlideStop(this.slideSound);
    this.slideSound = null;
    this.slideHaptics.endSlide();

    // Play lock feedback
    this.audioManager.playLock();
    this.haptics.lockPiece();

    // Play freeze exit animation
    this.animationSystem.playFreezeExitAnimation(this.activeShape.graphics);

    // Calculate and add score before losing reference
    const bodyY = this.activeShape.body.position.y;
    const bodyX = this.activeShape.body.position.x;
    const height = GAMEPLAY.PLATFORM_Y - bodyY;
    const centerOffset = Math.abs(bodyX - GAME.WIDTH / 2);
    const shapeType = this.activeShape.type;

    const points = this.scoreManager.calculatePlacementScore(
      shapeType,
      height,
      centerOffset
    );
    this.scoreManager.addScore(points);

    // Update HUD
    this.hud.updateScore(this.scoreManager.getCurrentScore());
    this.hud.updateHighScore(this.scoreManager.getHighScore());
    this.hud.updatePiecesCount(this.scoreManager.getPiecesPlaced());

    // Show score popup
    this.hud.showScorePopup(bodyX, bodyY, points);

    // Play placement success animation
    this.animationSystem.playPlacementSuccess(this.activeShape.graphics);

    // End slide
    this.slideSystem.endSlide();

    // Hide CoM indicator
    this.comIndicator.hide();

    // Hide slide hint
    this.hideSlideHint();

    // Unfreeze physics
    this.freezeSystem.unfreeze(this.activeShape.body);

    // Add to placed shapes list
    this.placedShapes.push(this.activeShape);
    this.activeShape = null;

    // Clear collision normal
    this.lastCollisionNormal = null;

    // Transition to stabilizing
    this.stateMachine.transition(GameState.STABILIZING);

    // Wait for physics to settle, then spawn next piece
    this.time.delayedCall(800, () => {
      if (this.isGameOver) return;
      this.stateMachine.transition(GameState.SPAWNING);
      this.spawnNextPiece();
    });
  }

  /**
   * Show visual feedback for freeze state
   */
  private showFreezeVisuals(): void {
    this.tweens.add({
      targets: this.freezeOverlay,
      alpha: 1,
      duration: 150,
    });

    if (this.activeShape) {
      this.activeShape.graphics.setAlpha(1);
      this.tweens.add({
        targets: this.activeShape.graphics,
        scaleX: 1.04,
        scaleY: 1.04,
        duration: 400,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }
  }

  /**
   * Hide freeze visuals
   */
  private hideFreezeVisuals(): void {
    this.tweens.add({
      targets: this.freezeOverlay,
      alpha: 0,
      duration: 150,
    });

    this.placedShapes.forEach((shape) => {
      this.tweens.killTweensOf(shape.graphics);
      shape.graphics.setScale(1, 1);
    });
  }

  /**
   * Show slide direction hint
   */
  private showSlideHint(): void {
    if (!this.activeShape) return;

    const tangent = this.slideSystem.getTangent();
    if (!tangent) return;

    this.slideHint.clear();
    this.slideHint.setVisible(true);

    const pos = this.activeShape.body.position;
    const arrowLength = 45;

    // Draw with softer style
    this.slideHint.lineStyle(3, 0xffffff, 0.6);

    const leftX = pos.x - tangent.x * arrowLength;
    const leftY = pos.y - tangent.y * arrowLength;
    this.slideHint.lineBetween(pos.x, pos.y, leftX, leftY);
    this.drawArrowHead(leftX, leftY, tangent.x, tangent.y);

    const rightX = pos.x + tangent.x * arrowLength;
    const rightY = pos.y + tangent.y * arrowLength;
    this.slideHint.lineBetween(pos.x, pos.y, rightX, rightY);
    this.drawArrowHead(rightX, rightY, -tangent.x, -tangent.y);
  }

  /**
   * Draw arrow head for slide hint
   */
  private drawArrowHead(x: number, y: number, dirX: number, dirY: number): void {
    const size = 10;
    const angle = Math.atan2(dirY, dirX);
    const angle1 = angle + Math.PI * 0.75;
    const angle2 = angle - Math.PI * 0.75;

    this.slideHint.lineBetween(
      x,
      y,
      x + Math.cos(angle1) * size,
      y + Math.sin(angle1) * size
    );
    this.slideHint.lineBetween(
      x,
      y,
      x + Math.cos(angle2) * size,
      y + Math.sin(angle2) * size
    );
  }

  /**
   * Hide slide hint
   */
  private hideSlideHint(): void {
    this.slideHint.setVisible(false);
    this.slideHint.clear();
  }

  /**
   * Update state indicator text
   */
  private updateStateText(text: string): void {
    this.stateText.setText(text);
  }

  /**
   * Spawn the next piece with animation
   */
  private spawnNextPiece(): void {
    this.activeShape = this.spawnManager.spawnRandom();

    // Play spawn sound
    this.audioManager.playSpawn();

    // Set depths for layering (shadow behind graphics)
    const depth = this.placedShapes.length + 10;
    this.activeShape.shadow.setDepth(depth);
    this.activeShape.graphics.setDepth(depth + 1);

    // Play spawn animation
    this.animationSystem.playSpawnAnimation(this.activeShape.graphics);
    this.animationSystem.playSpawnAnimation(this.activeShape.shadow);

    this.stateMachine.transition(GameState.FALLING);
  }

  /**
   * Update graphics to follow physics bodies
   */
  private updateShapeGraphics(): void {
    if (this.activeShape) {
      this.syncGraphicsToBody(this.activeShape);
    }

    for (const shape of this.placedShapes) {
      this.syncGraphicsToBody(shape);
    }
  }

  /**
   * Sync graphics position and rotation to physics body (including shadow)
   */
  private syncGraphicsToBody(shape: ShapeObject): void {
    const { body, graphics, shadow } = shape;

    // Main graphics follow body
    graphics.setPosition(body.position.x, body.position.y);
    graphics.setRotation(body.angle);

    // Shadow follows with offset
    shadow.setPosition(
      body.position.x + this.shadowOffset.x,
      body.position.y + this.shadowOffset.y
    );
    shadow.setRotation(body.angle);
  }

  /**
   * Get the platform body (for external use)
   */
  getPlatformBody(): MatterJS.BodyType {
    return this.platformBody;
  }

  /**
   * Cleanup when leaving scene
   */
  private cleanupScene(): void {
    // Stop all tweens
    this.tweens.killAll();

    // Resume physics if paused
    this.matter.world.resume();

    // Clear active shape
    if (this.activeShape) {
      this.matter.world.remove(this.activeShape.body);
      this.activeShape.graphics.destroy();
      this.activeShape.shadow.destroy();
      this.activeShape = null;
    }

    // Clear placed shapes
    for (const shape of this.placedShapes) {
      this.matter.world.remove(shape.body);
      shape.graphics.destroy();
      shape.shadow.destroy();
    }
    this.placedShapes = [];

    // Clear systems
    this.freezeSystem.clear();
    this.slideSystem.endSlide();
    this.comIndicator.destroy();
    this.heightTracker.destroy();
    this.hud.destroy();
    this.stateMachine.reset();

    // Clear visual systems
    this.background.destroy();
    this.paperTexture.destroy();
    this.animationSystem.destroy();

    // Clear audio and UI
    this.audioManager.destroy();
    this.settingsPanel.destroy();
  }
}
